<template>
  <div class="c-app" :class="{ 'c-dark-theme': $store.state.darkMode }">
    <TheSidebar/>
    <CWrapper>
      <TheHeader/>
      <ThePageTabs/>
      <div class="c-body">
        <main class="c-main">
          <CContainer fluid>
            <transition name="fade" mode="out-in">
              <router-view :key="$route.path"></router-view>
            </transition>
          </CContainer>
        </main>
      </div>
      <TheFooter/>
      
      <!-- ✨ MOVED: Quick Add Expense FAB - Inside CWrapper but Fixed Position -->
      <div 
        class="expense-fab-container" 
        :class="{ 
          'sidebar-open': $store.state.asideShow,
          'fab-shifted': $store.state.asideShow && isDesktop 
        }"
        v-if="shouldShowExpenseFAB"
      >
        <CButton 
          color="primary" 
          class="expense-fab"
          aria-label="Add expense (Ctrl+E)"
          @click="showQuickExpenseModal = true"
          v-c-tooltip="{ content: 'Add expense (Ctrl+E)', placement: 'right' }"
        >
          <CIcon name="cil-plus" size="lg" />
          <span class="fab-text d-none d-lg-inline ml-1">Expense</span>
        </CButton>
      </div>
    </CWrapper>
    
    <!-- N8N Chat Widget - Floating Chat -->
    <N8nChatWidget 
      v-if="showChatWidget && isWebhookReady"
      :webhook-url="dynamicWebhookUrl"
      mode="window"
      :show-welcome-screen="false"
      :initial-messages="contextualInitialMessages"
      :metadata="enrichedChatMetadata"
      :customization="currentCustomization"
      @chat-initialized="onChatInitialized"
      @chat-error="onChatError"
      @chat-loading="onChatLoading"
    />

    <!-- ✨ NEW: Quick Add Expense Modal - CoreUI v3.2 Standard -->
    <CModal
      title="Add expense"
      :show.sync="showQuickExpenseModal"
      @update:show="onExpenseModalClose"
    >
      <!-- Amount Input -->
      <CRow class="mb-3">
        <CCol>
          <label for="expense-amount" class="form-label font-weight-bold">Amount *</label>
          <div class="input-group">
            <div class="input-group-prepend">
              <span class="input-group-text">RM</span>
            </div>
            <CInput
              id="expense-amount"
              ref="expenseAmountInput"
              v-model="quickExpense.amount"
              type="number"
              step="0.01"
              placeholder="0.00"
              required
              @input="onAmountChange"
            />
          </div>
        </CCol>
      </CRow>

      <!-- Quick Vendor Selection -->
      <CRow class="mb-3">
        <CCol>
          <label class="form-label font-weight-bold">Recent vendors</label>
          <div class="vendor-buttons mb-2" v-if="quickVendors.length > 0">
            <CButton
              v-for="vendor in quickVendors"
              :key="vendor.vendorId"
              :color="quickExpense.vendorId === vendor.vendorId ? 'primary' : 'light'"
              size="sm"
              class="mr-2 mb-2 vendor-btn"
              @click="selectVendor(vendor)"
            >
              <div class="vendor-button-content">
                <div class="vendor-name">{{ vendor.vendorName }}</div>
                <small class="vendor-stats">
                  Used {{ vendor.usageCount }} {{ vendor.usageCount === 1 ? 'time' : 'times' }} · Average RM {{ formatMyr(vendor.averageAmount) }}
                </small>
              </div>
            </CButton>
          </div>
          <small class="text-muted" v-if="quickVendors.length === 0">
            No recent vendors yet. Type the vendor name below.
          </small>
        </CCol>
      </CRow>

      <!-- Custom Vendor Input -->
      <CRow class="mb-3">
        <CCol>
          <label for="expense-vendor" class="form-label font-weight-bold">
            Vendor name
            <small class="text-muted" v-if="quickExpense.vendorId">
              (selected: {{ quickExpense.vendorName }})
            </small>
          </label>
          <CInput
            id="expense-vendor"
            v-model="quickExpense.vendorName"
            placeholder="Vendor name"
            @input="onVendorNameChange"
          />
        </CCol>
      </CRow>

      <!-- Description -->
      <CRow class="mb-3">
        <CCol>
          <label for="expense-description" class="form-label font-weight-bold">Description</label>
          <CInput
            id="expense-description"
            v-model="quickExpense.description"
            :placeholder="descriptionSuggestion"
          />
        </CCol>
      </CRow>

      <!-- Date -->
      <CRow class="mb-3">
        <CCol>
          <label for="expense-date" class="form-label font-weight-bold">Date</label>
          <CInput
            id="expense-date"
            v-model="quickExpense.date"
            type="date"
          />
        </CCol>
      </CRow>

      <!-- Recent Expenses for Quick Duplicate -->
      <div class="recent-expenses-section mt-3" v-if="recentExpenses.length > 0">
        <small class="text-muted font-weight-bold">Or copy a recent expense</small>
        <CRow class="mt-2">
          <CCol
            v-for="recent in recentExpenses.slice(0, 3)"
            :key="recent.id"
            cols="4"
            class="mb-2"
          >
            <button
              type="button"
              class="btn btn-light btn-block text-left h-100 p-2"
              :aria-label="`Copy expense from ${(recent.vendor && recent.vendor.name) || 'unknown vendor'}, RM ${formatMyr(recent.totalAmount)}`"
              @click="duplicateRecentExpense(recent)"
            >
              <div class="recent-vendor text-truncate">
                <strong>{{ (recent.vendor && recent.vendor.name) || 'Unknown vendor' }}</strong>
              </div>
              <div class="recent-amount">
                <strong>RM {{ formatMyr(recent.totalAmount) }}</strong>
              </div>
              <small class="recent-category text-muted text-truncate d-block">
                {{ recent.description || 'No description' }}
              </small>
            </button>
          </CCol>
        </CRow>
      </div>

      <template #footer>
        <CButton color="light" class="mr-auto" :disabled="savingExpense" @click="showQuickExpenseModal = false">
          Cancel
        </CButton>
        <CButton
          color="primary"
          variant="outline"
          @click="saveAndAddAnother"
          :disabled="!isExpenseFormValid || savingExpense"
          class="ml-2"
        >
          Save and add another
        </CButton>
        <CButton
          color="primary"
          @click="saveQuickExpense"
          :disabled="!isExpenseFormValid || savingExpense"
          class="ml-2"
        >
          Add expense
        </CButton>
      </template>
    </CModal>

    <!-- Quick Add toasts + global this.$toast (plugins/toast.js) -->
    <CToaster :autohide="3000" placement="top-end">
      <template v-for="toast in expenseToasts.concat(globalToasts)">
        <CToast
          :key="toast.id"
          :show="true"
          :header="toast.header"
          :color="toast.color"
        >
          {{ toast.message }}
        </CToast>
      </template>
    </CToaster>
  </div>
</template>

<script>
import TheSidebar from './TheSidebar'
import TheHeader from './TheHeader'
import TheFooter from './TheFooter'
import ThePageTabs from './ThePageTabs'
import N8nChatWidget from '../views/widgets/N8nChatWidget.vue'
// ✨ NEW: Import Expense API
import ExpenseApi from '../lib/expenseApi.js'
import { toastState } from '../plugins/toast'
import { formatMyr } from '../utils/money'
import { mytToday } from '../utils/datetime'

export default {
  name: 'TheContainer',
  components: {
    TheSidebar,
    TheHeader,
    TheFooter,
    ThePageTabs,
    N8nChatWidget
  },
  data() {
    return {
      showChatWidget: false, // Temporarily disabled - switching automation service
      chatInitialized: false,
      isWebhookReady: false,
      sessionId: null,
      
      // ✨ NEW: Quick Expense Data
      showQuickExpenseModal: false,
      savingExpense: false,
      quickExpense: {
        amount: '',
        vendorId: '',
        vendorName: '',
        description: '',
        date: mytToday()
      },
      expenseToasts: [],
      recentExpenses: [],
      quickVendors: [], // ✨ NEW: Quick vendor list
      expenseApi: new ExpenseApi(),
      
      // Malaysian business expense categories - REMOVED
      // quickCategories: [...] - No longer needed
      
      // Base webhook configuration
      webhookConfig: {
        baseUrl: 'https://n8n.safa.com.my/webhook',
        webhookId: '5f1c0c82-0ff9-40c7-9e2e-b1a96ffe24cd',
        endpoint: 'chat'
      }
    }
  },
  
  async created() {
    await this.initializeComponent()
  },

  mounted() {
    // ✨ NEW: Setup keyboard shortcuts and load data
    this.setupKeyboardShortcuts()
    this.loadRecentExpenses()
    this.loadQuickVendors() // ✨ NEW: Load quick vendors
  },
  
  computed: {
    globalToasts() {
      return toastState.items
    },
    // ✨ NEW: Determine if FAB should be shown
    shouldShowExpenseFAB() {
      // Show FAB on all pages except specific ones where it might interfere
      const excludeRoutes = ['login', 'register', 'forgot-password', 'reset-password']
      const currentRoute = this.$route.name?.toLowerCase() || ''
      const shouldShow = !excludeRoutes.includes(currentRoute)
      
      return shouldShow
    },

    // ✨ NEW: Expense form validation
    isExpenseFormValid() {
      return this.quickExpense.amount && 
             parseFloat(this.quickExpense.amount) > 0 && 
             this.quickExpense.category;
    },

    // ✨ NEW: Check if we're on desktop
    isDesktop() {
      return window.innerWidth >= 992;
    },
    
    // ✨ NEW: Smart vendor suggestion
    vendorSuggestion() {
      const category = this.quickCategories.find(c => c.name === this.quickExpense.category);
      if (category && category.vendors.length > 0) {
        return category.vendors[0];
      }
      return 'Enter vendor name';
    },
    
    // ✨ NEW: Smart description suggestion
    descriptionSuggestion() {
      const amount = parseFloat(this.quickExpense.amount);
      const category = this.quickExpense.category;
      
      if (category === 'Utilities' && amount >= 200 && amount <= 500) {
        return 'Electricity bill';
      } else if (category === 'Utilities' && amount >= 50 && amount <= 150) {
        return 'Water bill';
      } else if (category === 'Rent' && amount >= 1000) {
        return 'Office rent';
      } else if (category === 'Fuel' && amount >= 30 && amount <= 100) {
        return 'Petrol';
      } else if (category === 'Meals' && amount >= 5 && amount <= 50) {
        return 'Team lunch';
      }
      
      return `${category} expense`;
    },

    // Build dynamic webhook URL with parameters
    dynamicWebhookUrl() {
      if (!this.isWebhookReady) return ''
      
      try {
        const { baseUrl, webhookId, endpoint } = this.webhookConfig
        const baseWebhookUrl = `${baseUrl}/${webhookId}/${endpoint}`
        
        // Build query parameters
        const params = new URLSearchParams()
        
        // Add business context
        if (this.currentUser?.businessId) {
          params.append('businessId', this.currentUser.businessId)
        }
        
        // Add profile context
        if (this.currentUser?.profileId) {
          params.append('profileId', this.currentUser.profileId)
        }
        
        // Add user role
        if (this.currentUser?.role) {
          params.append('role', this.currentUser.role)
        }
        
        const queryString = params.toString()
        const finalUrl = queryString ? `${baseWebhookUrl}?${queryString}` : baseWebhookUrl
        
        return finalUrl
        
      } catch (error) {
        console.error('Error building webhook URL:', error)
        return `${this.webhookConfig.baseUrl}/${this.webhookConfig.webhookId}/${this.webhookConfig.endpoint}`
      }
    },
    
    // Get current user from store with fallbacks
    currentUser() {
      let localUser = auth.getLocalUser();
      if (localUser) {
        return {
          profileId: localUser.profileId || '',
          businessId: localUser.profile.defaultBusinessId || '',
          name: localUser.profile.fullName || '???',
          userName: localUser.userName || '',
          role: localUser.role || 'visitor',
          email: localUser.email || null
        }
      }
      return null;
    },
    
    // Contextual initial messages based on user/business
    contextualInitialMessages() {
      const userName = this.currentUser?.name || 'there'
      const businessName = 'Niaga-Ku'
      
      const messages = [
        `Hello ${userName}! 👋`,
        `Welcome to ${businessName} support chat!`
      ]
      
      // Add role-specific messaging
      if (this.currentUser?.role) {
        switch (this.currentUser.role.toLowerCase()) {
          case 'admin':
          case 'administrator':
            messages.push('As an administrator, I can help you with system management and advanced features.')
            break
          case 'manager':
            messages.push('I can assist you with management tools and reporting features.')
            break
          case 'user':
          case 'client':
            messages.push('How can I help you today?')
            break
          default:
            messages.push('How can I assist you today?')
        }
      } else {
        messages.push('How can I help you today?')
      }
      
      return messages
    },
    
    // Enhanced metadata with all available context
    enrichedChatMetadata() {
      const metadata = {
        // User context
        userId: this.currentUser?.id || 'anonymous',
        userName: this.currentUser?.name || 'Guest',
        userEmail: this.currentUser?.email || null,
        userRole: this.currentUser?.role || 'visitor',
        
        // Business context
        businessId: this.currentUser?.businessId || null,
        
        // Profile context
        profileId: this.currentUser?.profileId || null,
        
        // Application context
        source: 'vue-app',
        currentRoute: this.$route?.name || 'unknown',
        currentPath: this.$route?.path || '/',
        
        // Environment context
        appVersion: process.env.VUE_APP_VERSION || '1.0.0',
        environment: process.env.NODE_ENV || 'production',
        darkMode: this.$store.state.darkMode,
        
        // Session context
        sessionId: this.sessionId,
        timestamp: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        language: navigator.language || 'en-US'
      }
      
      // Remove null/undefined values to keep metadata clean
      return Object.fromEntries(
        Object.entries(metadata).filter(([, value]) => value != null)
      )
    },
    
    // Dynamic customization based on dark mode and business branding
    currentCustomization() {
      const baseCustomization = {
        windowWidth: '380px',
        windowHeight: '600px',
        borderRadius: '12px',
        toggleSize: '60px'
      }
      
      if (this.$store.state.darkMode) {
        return {
          ...baseCustomization,
          primaryColor: '#4f46e5',
          secondaryColor:'#06b6d4',
          backgroundColor: '#1f2937',
          textColor: '#f9fafb',
          headerBackground:  '#111827',
          headerColor: '#f9fafb'
        }
      } else {
        return {
          ...baseCustomization,
          primaryColor:  '#667eea',
          secondaryColor:  '#764ba2',
          backgroundColor: '#ffffff',
          textColor: '#1f2937',
          headerBackground:  '#667eea',
          headerColor: '#ffffff'
        }
      }
    }
  },
  
  methods: {
    // ✨ NEW: Expense Management Methods
    async loadRecentExpenses() {
      try {
        const response = await this.expenseApi.getListByCurrentBusiness({ 
          page: 1, 
          pageSize: 10,
          sortBy: 'date',
          sortDirection: 'desc'
        });
        this.recentExpenses = response.data || [];
      } catch (error) {
        console.error('Error loading recent expenses:', error);
      }
    },

    // ✨ NEW: Load quick vendors
    async loadQuickVendors() {
      try {
        const response = await this.expenseApi.getQuickVendors();
        this.quickVendors = response.result || [];
      } catch (error) {
        console.error('Error loading quick vendors:', error);
        this.quickVendors = [];
      }
    },
    
    setupKeyboardShortcuts() {
      document.addEventListener('keydown', (e) => {
        // Ctrl/Cmd + E = Quick add expense
        if ((e.ctrlKey || e.metaKey) && e.key === 'e' && !e.shiftKey) {
          e.preventDefault();
          this.showQuickExpenseModal = true;
          this.$nextTick(() => {
            this.$refs.expenseAmountInput?.focus();
          });
        }
      });
    },
    
    selectVendor(vendor) {
      this.quickExpense.vendorId = vendor.vendorId;
      this.quickExpense.vendorName = vendor.vendorName;
      
      // Auto-suggest amount based on vendor's average
      if (!this.quickExpense.amount && vendor.averageAmount > 0) {
        this.quickExpense.amount = vendor.averageAmount.toFixed(2);
      }
    },
    
    onAmountChange() {
      // Auto-suggest vendor based on amount (if no vendor selected)
      if (!this.quickExpense.vendorId && this.quickVendors.length > 0) {
        const amount = parseFloat(this.quickExpense.amount);
        
        // Find vendor with closest average amount
        const closestVendor = this.quickVendors.reduce((prev, curr) => {
          const prevDiff = Math.abs(prev.averageAmount - amount);
          const currDiff = Math.abs(curr.averageAmount - amount);
          return currDiff < prevDiff ? curr : prev;
        });
        
        // Only auto-select if the difference is reasonable (within 50%)
        const amountDiff = Math.abs(closestVendor.averageAmount - amount);
        const threshold = closestVendor.averageAmount * 0.5;
        
        if (amountDiff <= threshold) {
          this.selectVendor(closestVendor);
        }
      }
    },
    
    onVendorNameChange() {
      // Clear vendor ID if user types custom vendor name
      this.quickExpense.vendorId = '';
    },
    
    duplicateRecentExpense(recent) {
      this.quickExpense = {
        amount: recent.totalAmount.toFixed(2),
        vendorId: recent.vendorId || '',
        vendorName: recent.vendor?.name || '',
        description: recent.description || '',
        date: mytToday()
      };
    },
    
    formatMyr,

    async saveQuickExpense() {
      if (this.savingExpense) return;
      this.savingExpense = true;
      try {
        const expenseData = {
          totalAmount: parseFloat(this.quickExpense.amount),
          description: this.quickExpense.description || this.descriptionSuggestion,
          date: this.quickExpense.date,
          vendorId: this.quickExpense.vendorId || null
        };
        
        // If no vendor ID selected but vendor name provided, handle as custom vendor
        if (!expenseData.vendorId && this.quickExpense.vendorName) {
          // You might want to create a new vendor or handle this case
          // For now, just include the vendor name in description
          expenseData.description = `${this.quickExpense.vendorName} - ${expenseData.description}`;
        }
        
        await this.expenseApi.create(expenseData);
        
        this.showExpenseToast('Success', 'Expense added', 'success');
        this.resetExpenseForm();
        this.showQuickExpenseModal = false;
        this.loadRecentExpenses(); // Refresh recent expenses
        this.loadQuickVendors(); // Refresh quick vendors (usage counts may change)
        
      } catch (error) {
        this.showExpenseToast('Error', "Couldn't add the expense. Check your connection and try again.", 'danger');
        console.error('Error saving expense:', error);
      } finally {
        this.savingExpense = false;
      }
    },
    
    async saveAndAddAnother() {
      await this.saveQuickExpense();
      if (!this.showQuickExpenseModal) { // If save was successful
        this.showQuickExpenseModal = true;
        this.$nextTick(() => {
          this.$refs.expenseAmountInput?.focus();
        });
      }
    },
    
    resetExpenseForm() {
      this.quickExpense = {
        amount: '',
        vendorId: '',
        vendorName: '',
        description: '',
        date: mytToday()
      };
    },
    
    onExpenseModalClose() {
      this.resetExpenseForm();
    },
    
    showExpenseToast(header, message, color) {
      this.expenseToasts.push({
        id: Date.now(),
        header,
        message,
        color
      });
    },

    // Initialize component with proper async handling
    async initializeComponent() {
      try {
        // Generate session ID
        this.generateSessionId()
        
        // Wait for store to be ready
        await this.waitForStoreReady()
        
        // Validate webhook configuration
        this.validateWebhookConfig()
        
        // Mark as ready
        this.isWebhookReady = true
        
      } catch (error) {
        console.error('Failed to initialize chat component:', error)
        this.handleInitializationError(error)
      }
    },
    
    // Wait for store to have necessary data
    waitForStoreReady(timeout = 5000) {
      return new Promise((resolve, reject) => {
        const startTime = Date.now()
        
        const checkStore = () => {
          // Check if we have at least basic user data
          let localUser = auth.getLocalUser();
          if (localUser && localUser.profile && localUser.profile.defaultBusinessId) {
            resolve()
            return
          }
          
          // Timeout check
          if (Date.now() - startTime > timeout) {
            resolve()
            return
          }
          
          // Check again in 100ms
          setTimeout(checkStore, 100)
        }
        
        checkStore()
      })
    },
    
    // Validate webhook configuration
    validateWebhookConfig() {
      const { baseUrl, webhookId, endpoint } = this.webhookConfig
      
      if (!baseUrl || !webhookId || !endpoint) {
        throw new Error('Invalid webhook configuration')
      }
      
      // Validate URL format
      try {
        new URL(`${baseUrl}/${webhookId}/${endpoint}`)
      } catch (error) {
        throw new Error('Invalid webhook URL format')
      }
    },
    
    // Generate unique session ID
    generateSessionId() {
      if (!this.sessionId) {
        const timestamp = Date.now()
        const random = Math.random().toString(36).substr(2, 9)
        const userId = this.currentUser?.profileId || 'anon'
        this.sessionId = `${userId}_${timestamp}_${random}`
      }
      return this.sessionId
    },
    
    // Chat event handlers
    onChatInitialized(chatInstance) {
      this.chatInitialized = true
      
      // Store chat instance in Vuex if needed
      if (this.$store.commit) {
        this.$store.commit('setChatInstance', chatInstance)
      }
      
      // Track analytics event
      this.trackChatEvent('chat_initialized', {
        userId: this.currentUser?.profileId,
        businessId: this.currentUser?.businessId,
        sessionId: this.sessionId
      })
      
      // Show success notification
      this.showSuccessNotification('Chat support is now available!')
    },
    
    onChatError(error) {
      console.error('N8N Chat initialization failed:', error)
      this.chatInitialized = false
      
      // Track error event
      this.trackChatEvent('chat_error', {
        error: error.message,
        userId: this.currentUser?.profileId,
        sessionId: this.sessionId
      })
      
      // Handle error gracefully
      this.handleChatError(error)
    },
    
    onChatLoading(isLoading) {
      // Could update global loading state if needed
    },
    
    // Error handling
    handleInitializationError(error) {
      console.error('Chat initialization error:', error)
      this.showErrorNotification('Unable to initialize chat. Please refresh the page.')
    },
    
    handleChatError(error) {
      const isNetworkError = error.message.includes('network') || error.message.includes('fetch')
      
      if (isNetworkError) {
        this.showErrorNotification('Network error. Please check your connection and try again.')
      } else {
        this.showErrorNotification('Chat service is temporarily unavailable. Please try again later.')
      }
      
      // Auto-hide chat widget after persistent errors
      setTimeout(() => {
        if (!this.chatInitialized) {
          this.showChatWidget = false
        }
      }, 10000)
    },
    
    // Notification methods (integrate with your notification system)
    showSuccessNotification(message) {
      // Integrate with your existing notification system
      if (this.$toast?.success) {
        this.$toast.success(message, {
          position: 'top-right',
          timeout: 3000
        })
      }
    },
    
    showErrorNotification(message) {
      // Integrate with your existing notification system
      if (this.$toast?.error) {
        this.$toast.error(message, {
          position: 'top-right',
          timeout: 5000
        })
      } else {
        console.error('ERROR:', message)
      }
    },
    
    // Analytics tracking
    trackChatEvent(eventName, properties = {}) {
      // Integrate with your analytics service (GA, Mixpanel, etc.)
      if (typeof gtag !== 'undefined') {
        gtag('event', eventName, properties)
      } else if (typeof analytics !== 'undefined') {
        analytics.track(eventName, properties)
      }
    },
    
    // Utility methods for external control
    toggleChatWidget() {
      this.showChatWidget = !this.showChatWidget
      
      this.trackChatEvent('chat_toggled', {
        visible: this.showChatWidget,
        userId: this.currentUser?.profileId
      })
    },
    
    refreshChatWidget() {
      this.showChatWidget = false
      this.chatInitialized = false
      
      this.$nextTick(() => {
        this.showChatWidget = true
      })
      
      this.trackChatEvent('chat_refreshed', {
        userId: this.currentUser?.profileId,
        sessionId: this.sessionId
      })
    },
    
    // Programmatically update webhook parameters
    updateWebhookParams(newParams = {}) {
      // This would trigger a reactive update of the webhook URL
      // Useful for when business/profile context changes
      this.$forceUpdate()
      
    }
  },
  
  watch: {
    // React to route changes
    '$route'(newRoute, oldRoute) {
      // Track page view for chat context
      this.trackChatEvent('page_view', {
        from: oldRoute?.name,
        to: newRoute?.name,
        userId: this.currentUser?.profileId
      })
    },
    
    // React to dark mode changes
    '$store.state.darkMode'(newValue) {
      this.trackChatEvent('theme_changed', {
        darkMode: newValue,
        userId: this.currentUser?.profileId
      })
    },
    
    // React to user context changes
    currentUser: {
      handler(newUser, oldUser) {
        if (newUser?.profileId !== oldUser?.profileId) {
          // Generate new session ID for new user
          this.sessionId = null
          this.generateSessionId()
          
          // Refresh chat with new context
          this.refreshChatWidget()
        }
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Ensure chat widget doesn't interfere with layout */
.c-app {
  position: relative;
}

/* ✨ NEW: Expense FAB Styles - Fixed Position with Sidebar Awareness */
.expense-fab-container {
  position: fixed !important;
  bottom: 20px !important;
  left: 20px !important;
  z-index: 1050 !important;
  display: block !important;
  pointer-events: auto !important;
  transition: left 0.3s ease !important;
}

/* When sidebar is open on desktop, shift FAB to the right */
.expense-fab-container.fab-shifted {
  left: 320px !important; /* Adjust based on your aside width */
}

.expense-fab {
  width: 60px !important;
  height: 60px !important;
  border-radius: 50% !important;
  border: none !important;
  transition: background-color 0.2s ease !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background-color: #10b981 !important;
  color: white !important;
}

.expense-fab:hover {
  background-color: #059669 !important;
}

/* Desktop FAB - Expanded with text */
@media (min-width: 992px) {
  .expense-fab {
    width: auto !important;
    min-width: 60px !important;
    border-radius: 30px !important;
    padding: 12px 20px !important;
  }
  
  .fab-text {
    display: inline !important;
  }
}

/* Mobile positioning - no shifting needed as sidebar is overlaid */
@media (max-width: 991.98px) {
  .expense-fab-container.fab-shifted {
    left: 20px !important; /* Override shifting on mobile */
  }
  
  .expense-fab-container {
    bottom: 15px !important;
    left: 15px !important;
  }
  
  .expense-fab {
    width: 50px !important;
    height: 50px !important;
  }
  
  .fab-text {
    display: none !important;
  }
}

/* Vendor Button Styles */
.vendor-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.vendor-btn {
  transition: all 0.2s ease;
  border: 2px solid transparent;
  min-height: 60px;
  padding: 8px 12px;
}

.vendor-button-content {
  text-align: left;
  line-height: 1.2;
}

.vendor-name {
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 2px;
}

.vendor-stats {
  font-size: 0.75rem;
  color: #6c757d;
  display: block;
}

/* Recent Expenses Styles */
.recent-expenses-section {
  border-top: 1px solid #dee2e6;
  padding-top: 1rem;
}

.recent-expenses-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 8px;
}

.recent-expense-item {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.recent-expense-item:hover {
  background: #e9ecef;
  border-color: #007bff;
}

.recent-vendor {
  font-weight: 600;
  font-size: 0.9rem;
  color: #495057;
}

.recent-amount {
  font-size: 1rem;
  font-weight: 700;
  color: #007bff;
  margin: 4px 0;
}

.recent-category {
  font-size: 0.8rem;
  color: #6c757d;
}

/* Form Improvements */
.form-group label {
  margin-bottom: 0.5rem;
}

/* Modal form styling */
.modal .form-group {
  margin-bottom: 1rem;
}

.modal .form-control {
  display: block;
  width: 100%;
  height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.modal .form-control:focus {
  color: #495057;
  background-color: #fff;
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.modal .input-group-text {
  display: flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  margin-bottom: 0;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #495057;
  text-align: center;
  white-space: nowrap;
  background-color: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}

.modal .input-group-prepend .input-group-text {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.modal .input-group-prepend + .form-control {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.modal .input-group {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  width: 100%;
}

/* Dark mode support for FAB */
.c-dark-theme .expense-fab {
  background-color: #4f46e5;
  color: white;
}

.c-dark-theme .expense-fab:hover {
  background-color: #4338ca;
}

.c-dark-theme .recent-expense-item {
  background: #374151;
  border-color: #4b5563;
  color: #f9fafb;
}

.c-dark-theme .recent-expense-item:hover {
  background: #4b5563;
  border-color: #6366f1;
}

.c-dark-theme .recent-vendor {
  color: #f9fafb;
}

.c-dark-theme .recent-amount {
  color: #60a5fa;
}

.c-dark-theme .recent-category {
  color: #9ca3af;
}

/* Mobile Responsiveness */
@media (max-width: 767.98px) {
  .expense-fab-container {
    bottom: 15px;
    left: 15px; /* Consistent with above */
  }
  
  .expense-fab {
    width: 50px;
    height: 50px;
  }
  
  .fab-text {
    display: none !important;
  }
  
  .category-buttons {
    justify-content: center;
  }
  
  .recent-expenses-grid {
    grid-template-columns: 1fr;
  }
}

/* Ensure FAB doesn't conflict with chat widget */
.expense-fab-container {
  pointer-events: auto;
}

/* Modal improvements */
.modal-dialog {
  margin-top: 3rem;
}

@media (max-width: 575.98px) {
  .modal-dialog {
    margin: 1rem;
  }
}
</style>